import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Code2, CheckCircle2, AlertTriangle, Save, ExternalLink, BarChart3 } from "lucide-react";

const GTM_REGEX = /^GTM-[A-Z0-9]{5,10}$/;

const TRACKED_EVENTS = [
  { event: "page_view", desc: "Navegacion entre paginas publicas", scope: "Todas las paginas" },
  { event: "ascauto_cta", desc: "Clic en CTAs principales", scope: "Home, servicios, conocenos, contacto" },
  { event: "cta_whatsapp", desc: "Clic en boton de WhatsApp", scope: "Todas las paginas publicas" },
  { event: "cta_call", desc: "Clic en telefono / Llamar ahora", scope: "Contacto, centros, header" },
  { event: "cta_promotion", desc: "Clic en promocion o reserva desde promo", scope: "Home, promociones" },
  { event: "appointment_start", desc: "Inicio del formulario de cita", scope: "/cita" },
  { event: "appointment_submit", desc: "Envio del formulario de cita", scope: "/cita" },
  { event: "contact_form_submit", desc: "Envio del formulario de contacto", scope: "/contacto" },
  { event: "job_application_submit", desc: "Envio de candidatura", scope: "/trabaja-con-nosotros" },
  { event: "vehicle_view", desc: "Detalle de vehiculo", scope: "/vehiculos/:id" },
  { event: "vehicle_catalog", desc: "Navegacion al catalogo", scope: "/vehiculos" },
  { event: "center_directions", desc: "Clic en Como llegar", scope: "Home, conocenos, contacto" },
  { event: "home_conocenos", desc: "Clic en bloque Conocenos de la Home", scope: "/" },
  { event: "conocenos_cta_final", desc: "Clic en CTA final de Conocenos", scope: "/conocenos" },
];

export default function GtmConfigPage() {
  const { axiosAuth } = useAuth();
  const [config, setConfig] = useState({ gtm_id: "", active: false, environment: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axiosAuth().get("/gtm/config").then(r => {
      setConfig({ gtm_id: r.data.gtm_id || "", active: r.data.active || false, environment: r.data.environment || "" });
    }).catch(() => {}).finally(() => setLoading(false));
  }, [axiosAuth]);

  const isValid = !config.gtm_id || GTM_REGEX.test(config.gtm_id.trim().toUpperCase());

  const save = async () => {
    if (config.gtm_id && !GTM_REGEX.test(config.gtm_id.trim().toUpperCase())) {
      toast.error("Formato no valido. Debe ser GTM-XXXXXXX");
      return;
    }
    setSaving(true);
    try {
      const res = await axiosAuth().put("/gtm/config", {
        gtm_id: config.gtm_id.trim().toUpperCase(),
        active: config.active,
        environment: config.environment.trim(),
      });
      setConfig({ gtm_id: res.data.gtm_id, active: res.data.active, environment: res.data.environment || "" });
      toast.success("Configuracion GTM guardada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    }
    setSaving(false);
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin h-6 w-6 border-2 border-slate-900 border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="gtm-config-page">
      <div>
        <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2"><Code2 className="w-5 h-5 text-red-600" /> Google Tag Manager</h1>
        <p className="text-sm text-slate-500">Configura GTM para medir el rendimiento de la web publica con GA4, eventos y conversiones.</p>
      </div>

      {/* Config card */}
      <Card className="border-slate-200 rounded-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><Code2 className="w-4 h-4 text-red-600" /> Configuracion del contenedor</CardTitle>
          <CardDescription className="text-xs">El ID se obtiene en <a href="https://tagmanager.google.com/" target="_blank" rel="noopener noreferrer" className="text-red-600 underline">tagmanager.google.com</a></CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">GTM Container ID</Label>
            <Input
              value={config.gtm_id}
              onChange={e => setConfig(p => ({ ...p, gtm_id: e.target.value }))}
              placeholder="GTM-XXXXXXX"
              className={`rounded-sm h-10 border-slate-200 font-mono text-base ${config.gtm_id && !isValid ? "border-red-400 ring-1 ring-red-200" : ""}`}
              data-testid="gtm-id-input"
            />
            {config.gtm_id && (
              isValid ? (
                <p className="text-[10px] text-green-600 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Formato valido</p>
              ) : (
                <p className="text-[10px] text-red-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Formato no valido. Ejemplo: GTM-ABC1234</p>
              )
            )}
          </div>

          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-sm">
            <Switch checked={config.active} onCheckedChange={v => setConfig(p => ({ ...p, active: v }))} data-testid="gtm-active-switch" />
            <div>
              <Label className="text-sm font-semibold text-slate-900">GTM activo</Label>
              <p className="text-[10px] text-slate-500">Cuando esta activo, GTM se carga en todas las paginas publicas</p>
            </div>
            <div className="flex-1" />
            <Badge className={`rounded-sm text-xs ${config.active ? "bg-green-100 text-green-800 border-green-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>
              {config.active ? "Activo" : "Inactivo"}
            </Badge>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Entorno (opcional)</Label>
            <Input
              value={config.environment}
              onChange={e => setConfig(p => ({ ...p, environment: e.target.value }))}
              placeholder="Ej: produccion, staging..."
              className="rounded-sm h-9 border-slate-200 text-sm"
              data-testid="gtm-environment-input"
            />
            <p className="text-[10px] text-slate-400">Solo informativo. Util para distinguir entre entornos si tienes varios contenedores.</p>
          </div>

          <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm text-xs uppercase tracking-wider font-semibold" data-testid="gtm-save-btn">
            <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Guardando..." : "Guardar configuracion"}
          </Button>
        </CardContent>
      </Card>

      {/* Events reference */}
      <Card className="border-slate-200 rounded-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><BarChart3 className="w-4 h-4 text-red-600" /> Eventos disponibles en dataLayer</CardTitle>
          <CardDescription className="text-xs">Estos eventos se envian automaticamente al dataLayer cuando GTM esta activo. Puedes usarlos como activadores en GTM para enviar datos a GA4.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left p-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">Evento</th>
                  <th className="text-left p-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">Descripcion</th>
                  <th className="text-left p-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">Paginas</th>
                </tr>
              </thead>
              <tbody>
                {TRACKED_EVENTS.map(e => (
                  <tr key={e.event} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="p-2"><code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded font-mono text-red-700">{e.event}</code></td>
                    <td className="p-2 text-xs text-slate-700">{e.desc}</td>
                    <td className="p-2 text-xs text-slate-500">{e.scope}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Info */}
      <div className="bg-slate-50 border border-slate-200 p-4 rounded-sm">
        <h3 className="text-xs font-semibold text-slate-700 uppercase mb-2">Como configurar GA4 con GTM</h3>
        <ol className="text-xs text-slate-600 space-y-1.5 list-decimal pl-4">
          <li>Crea un contenedor en <a href="https://tagmanager.google.com/" target="_blank" rel="noopener noreferrer" className="text-red-600 underline">Google Tag Manager</a></li>
          <li>Copia el ID del contenedor (formato GTM-XXXXXXX) y pegalo arriba</li>
          <li>En GTM, crea una etiqueta de configuracion GA4 con tu Measurement ID (G-XXXXXXXX)</li>
          <li>Crea activadores para los eventos del dataLayer que quieras medir</li>
          <li>Publica el contenedor en GTM y activa GTM desde aqui</li>
        </ol>
      </div>
    </div>
  );
}
